import { useMemo } from 'react'
import { cn } from '#app/utils/tailwind-merge'
import { Button } from '../buttons/button'
import { Icon } from '../media/icon'

export enum PaginationItemType {
	DOTS = 'dots',
}

const range = (start: number, end: number) => {
	const length = end - start + 1
	return Array.from({ length }, (_, index) => index + start)
}

type PaginationProps = {
	total: number
	page: number // TODO: Restrict to positive numbers
	onChange?: (page: number) => void
	isDisabled?: boolean
	showControls?: boolean
	loop?: boolean
	controlled?: boolean
	siblings?: number
	boundaries?: number
	size?: 'sm' | 'md' | 'lg'
	className?: string
}

const Pagination = ({ total, page, onChange, siblings = 1, size, className }: PaginationProps) => {
	const paginationRange = useMemo(() => {
		const slots = 2 * siblings + 5
		if (slots >= total) {
			return range(1, total)
		}

		const leftSiblingIndex = Math.max(page - siblings, 1)
		const rightSiblingIndex = Math.min(page + siblings, total)
		const shouldShowLeftDots = leftSiblingIndex > 3
		const shouldShowRightDots = rightSiblingIndex < total - 2
		if (shouldShowLeftDots && shouldShowRightDots) {
			return [1, PaginationItemType.DOTS, ...range(leftSiblingIndex, rightSiblingIndex), PaginationItemType.DOTS, total]
		}
		if (shouldShowLeftDots && !shouldShowRightDots) {
			return [1, PaginationItemType.DOTS, ...range(total - slots + 3, total)]
		}
		if (!shouldShowLeftDots && shouldShowRightDots) {
			return [...range(1, slots - 2), PaginationItemType.DOTS, total]
		}
	}, [total, page, siblings])

	if (!paginationRange || paginationRange.length === 1) {
		return null
	}

	return (
		<nav role="navigation" aria-label="Pagination" className={cn('pagination', className)}>
			{paginationRange.map((pageNumber, index) => {
				const isBefore = index < paginationRange.indexOf(page)
				const isAfter = index > paginationRange.indexOf(page)
				return pageNumber === PaginationItemType.DOTS ?
						<Button
							key={`dots-${index}`}
							variant="secondary"
							size={size}
							onPress={() => {
								const newPage = Number(paginationRange[3 + siblings]) + (isBefore ? -siblings - 2 : siblings)
								onChange?.(newPage)
							}}
							data-left={isBefore || undefined}
							data-right={isAfter || undefined}
						>
							{({ isHovered }) =>
								!isHovered ? <Icon name="dots-horizontal" />
								: isBefore ? <Icon name="chevron-left" />
								: <Icon name="chevron-right" />
							}
						</Button>
					:	<Button key={`page-${pageNumber}`} variant="secondary" size={size} onPress={() => onChange?.(pageNumber)} isDisabled={pageNumber === page}>
							{pageNumber}
						</Button>
			})}
		</nav>
	)
}
Pagination.displayName = 'Pagination'

export { Pagination }
